import {Injectable} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';

import {StatusCode} from 'grpc-web';
import {Subject, Subscription, filter} from 'rxjs';

import {GrpcWebError} from 'frontend/typings/grpc_typings';
import {capitalize, truncate} from 'frontend/utils/string';

import type {FeedbackAndAnalyticsService} from './feedback_and_analytics_service';

export const DEFAULT_ERROR_MESSAGE =
  'gRPC has failed, please check the browser console for more information';

export const UNKNOWN_ERROR_MESSAGE =
  'An unknown error occurred. If you have been logged out, please refresh the page to log back in.';
export const SESSION_EXPIRED_MESSAGE =
  'Your session has expired. Please refresh the page to log back in.';
export const STREAM_FALLBACK_MESSAGE =
  'Unable to stream simulation updates. Falling back to periodic polling.';
export const SERVICE_UNAVAILABLE_MESSAGE =
  'The service is temporarily unavailable. We are retrying...';
export const UNKNOWN_CONTENT_TYPE_ERROR = 'Unknown Content-type received.';
export const API_FETCH_USER_CONFIG = 'FetchUserConfig';
export const API_FETCH_USER_ORG_CONFIG = 'FetchUserOrganizationConfig';
export const API_SUBSCRIBE = 'Subscribe';

const ERROR_MESSAGES_BY_CODE = new Map<number, string>([
  [StatusCode.CANCELLED, 'Cancelled'],
  [StatusCode.UNKNOWN, 'Unknown'],
  [StatusCode.INVALID_ARGUMENT, 'Invalid argument'],
  [StatusCode.DEADLINE_EXCEEDED, 'Deadline exceeded'],
  [StatusCode.INTERNAL, 'Internal error'],
  [StatusCode.NOT_FOUND, 'Not found error'],
  [StatusCode.PERMISSION_DENIED, 'Permission denied'],
  [StatusCode.UNAUTHENTICATED, 'Unauthenticated'],
  [StatusCode.FAILED_PRECONDITION, 'Failed preconditions'],
  [StatusCode.UNIMPLEMENTED, 'Unimplemented'],
  [StatusCode.UNAVAILABLE, 'Unavailable'],
]);

const ERROR_MESSAGE_MAX_LENGTH = 100;

/**
 * Represents an error that can occur within the application.
 * This interface can be used to represent both server-side and
 * client-side errors.
 */
export declare interface AppError {
  /**
   * A human-readable error message.
   */
  message: string;
  /**
   * The name of the API endpoint associated with the error,
   * if applicable. E.g.: DeleteChange, UpdateChange, etc.
   */
  apiName?: string;
  /**
   * The error stack trace, providing detailed information
   * about the error's origin, if applicable.
   */
  stack?: string;
  /**
   * Whether the error is critical and should block the application.
   */
  isCritical?: boolean;
}

/** Google Analytics event action type. */
enum EventActionType {
  ERROR = 'Error',
}

/**
 * Service to store and process application errors.
 */
@Injectable({providedIn: 'root'})
export class ErrorsService {
  private readonly subscription = new Subject<AppError>();
  // Derived directly from the error stream so there is a single source of
  // truth: the latest error flagged isCritical becomes the critical error.
  readonly criticalError = toSignal<AppError | null>(
    this.subscription.pipe(filter((error): error is AppError => !!error.isCritical)),
    {initialValue: null},
  );

  setError(error: string, tracking?: FeedbackAndAnalyticsService, isCritical = false) {
    if (tracking) {
      tracking.sendEvent(EventActionType.ERROR, {
        type: 'client',
        message: truncate(error, ERROR_MESSAGE_MAX_LENGTH),
      });
    }
    const appError: AppError = {message: error, isCritical};
    this.subscription.next(appError);
  }

  setGrpcWebError(
    error: GrpcWebError,
    apiName = '',
    tracking?: FeedbackAndAnalyticsService,
    isCritical = false,
  ) {
    const shortName = apiName.split('/').pop() || '';
    const normalizedShortName = shortName ? capitalize(shortName) : '';

    if (tracking) {
      tracking.sendEvent(EventActionType.ERROR, {
        type: 'grpc',
        message: truncate(
          grpcWebErrorToMessage(error, normalizedShortName),
          ERROR_MESSAGE_MAX_LENGTH,
        ),
      });
    }

    // eslint-disable-next-line no-console
    console.error(`❌ gRPC Error in [${apiName || 'Unknown API'}]:`, error);

    this.subscription.next({
      message: grpcWebErrorToMessage(error, normalizedShortName),
      apiName: normalizedShortName,
      stack: error.message,
      isCritical,
    });
  }

  subscribe(fn: (error: AppError) => void, isDebugMode = false): Subscription {
    return this.subscription.subscribe((error: AppError) => {
      // For non-debug mode, log the API name to the console for debugging purposes.
      if (!isDebugMode && error.apiName) {
        // eslint-disable-next-line no-console
        console.error(error.apiName);
      }
      fn(error);
    });
  }
}

/**
 * Converts a gRPC Web error to a user-friendly message.
 *
 * @param error The gRPC Web error object.
 * @param apiName The name of the API endpoint associated with the error.
 * @returns A user-friendly error message.
 */
function grpcWebErrorToMessage(error: GrpcWebError, apiName = ''): string {
  if (error.message && error.message.includes(UNKNOWN_CONTENT_TYPE_ERROR)) {
    if (apiName === API_FETCH_USER_CONFIG || apiName === API_FETCH_USER_ORG_CONFIG) {
      return SESSION_EXPIRED_MESSAGE;
    }
    if (apiName === API_SUBSCRIBE) {
      return STREAM_FALLBACK_MESSAGE;
    }
    return SERVICE_UNAVAILABLE_MESSAGE;
  }

  // A temporary solution to handle 401 error as UNKNOWN error.
  if (error.code === StatusCode.UNKNOWN) {
    return UNKNOWN_ERROR_MESSAGE;
  }

  let parsedMessage =
    error.message
      // The error message can contain the entire stack trace.
      // Attempt to parse the error by retrieving the first line
      // from the stack trace, which typically includes a
      // human-readable message.
      ?.split('\n')
      .filter(Boolean)
      .at(0) ?? '';

  // The error message can come from java code. Attempt to
  // match and remove the initial part of a Java exception
  // message, typically the exception class and package name
  // (e.g., ‘java.lang.IllegalArgumentException: some error’).
  if (parsedMessage.startsWith('java.lang.')) {
    parsedMessage = parsedMessage?.split(':').slice(1).join(':');
  }

  const message = parsedMessage ? capitalize(parsedMessage.trim()) : DEFAULT_ERROR_MESSAGE;

  return error.code && ERROR_MESSAGES_BY_CODE.has(error.code)
    ? `(${ERROR_MESSAGES_BY_CODE.get(error.code)!}) ${message}`
    : message;
}
